import { store } from "@redux"
import axios from "axios"

import {
  isInPosDevice,
  normalizeStoragePayload,
  saveTokensInLocaleStorage,
  userLogout
} from "utils"
import { refreshAccessToken } from "./accountService"

const trimTrailingSlash = (value = "") => value.replace(/\/+$/, "")

const API_AUTH_URL =
  process.env.REACT_APP_API_AUTH_URL || "https://api.nobatoon.ir/auth"

const API_MAIN_URL =
  process.env.REACT_APP_API_MAIN_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.nobatoon.ir/main"

const API_ANALYTIC_URL =
  process.env.REACT_APP_API_ANALYTIC_URL || "https://api.nobatoon.ir/analytic"

const API_STORAGE_URL =
  process.env.REACT_APP_API_STORAGE_URL || "https://api.nobatoon.ir/storage"

const API_SUBSCRIBE_URL =
  process.env.REACT_APP_API_SUBSCRIBE_URL || "https://api.nobatoon.ir/subscribe"

let dhFoods
let dhFoodsAuth
let dhFoodsStorage
let dhFoodsSubscribe

let isRefreshing = false
let failedQueue = []

const getApiOrigin = (domain, serviceUrl = API_MAIN_URL) => {
  if (serviceUrl) {
    try {
      return new URL(serviceUrl).origin
    } catch (err) {
      return trimTrailingSlash(serviceUrl)
        .replace(/\/(auth|main|storage|subscribe)$/, "")
    }
  }

  return `https://api.${domain}`
}

const getServiceBaseUrl = (domain, serviceName, envUrl) => {
  if (envUrl) {
    return `${trimTrailingSlash(envUrl)}/`
  }

  return `${getApiOrigin(domain)}/${serviceName}/`
}

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    error ?
      prom.reject(error) :
      prom.resolve(token)
  })
  failedQueue = []
}

const waitingForToken = () => (
  new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject })
  })
)

const handleError = async (error, api) => {
  if (!error.response) return Promise.reject(error)

  const { status, config: originalRequest } = error.response

  if (status === 401 && !originalRequest._retry) {
    if (isRefreshing) {
      try {
        const accessToken = await waitingForToken()

        originalRequest.headers["X-Access-Token"] = `Bearer ${accessToken}`

        return api(originalRequest)
      } catch (err) {
        return Promise.reject(err)
      }
    }

    originalRequest._retry = true

    isRefreshing = true

    try {
      const { accessToken, refreshToken } =
        await refreshAccessToken()

      saveTokensInLocaleStorage(
        accessToken,
        refreshToken
      )

      originalRequest.headers["X-Access-Token"] =
        `Bearer ${accessToken}`

      processQueue(null, accessToken)

      return api(originalRequest)
    } catch (err) {
      if (err?.response?.status === 404) {
        processQueue(err, null)

        userLogout()

        window.location.href = "/"
      }

      return Promise.reject(error)
    } finally {
      isRefreshing = false
    }
  }

  return Promise.reject(error)
}

const normalizeResponse = response => ({
  ...response,
  data: normalizeStoragePayload(response.data)
})

const handleRequest = req => {
  const {
    tokens: { accessToken },
    systemSettings: { subdomain }
  } = store.getState()

  if (!!accessToken) {
    req.headers.common["X-Access-Token"] = `Bearer ${accessToken}`
  }

  req.headers.common["X-Service-Client-Name"] = subdomain

  if (isInPosDevice()) {
    const random = String(Date.now())

    req.params = { ...req.params, random }
  }

  return req
}

const resetApi = () => {
  dhFoods = null
  dhFoodsStorage = null
}

const initApi = (domain) => {
  if (!!dhFoods) return

  dhFoodsAuth = axios.create({
    baseURL: `${trimTrailingSlash(API_AUTH_URL)}/`
  })

  dhFoodsAuth.interceptors.request.use(handleRequest)
  dhFoodsAuth.interceptors.response.use(
    normalizeResponse,
    error => handleError(error, dhFoodsAuth)
  )

  dhFoods = axios.create({
    baseURL: `${trimTrailingSlash(API_MAIN_URL)}/`
  })
  dhFoods.interceptors.request.use(handleRequest)
  dhFoods.interceptors.response.use(
    normalizeResponse,
    error => handleError(error, dhFoods)
  )

  dhFoodsStorage = axios.create({
    baseURL: `${trimTrailingSlash(API_STORAGE_URL)}/`
  })
  dhFoodsStorage.interceptors.request.use(handleRequest)
  dhFoodsStorage.interceptors.response.use(
    response => response,
    error => handleError(error, dhFoodsStorage)
  )

  dhFoodsSubscribe = axios.create({
    baseURL: `${trimTrailingSlash(API_SUBSCRIBE_URL)}/`
  })
  dhFoodsSubscribe.interceptors.request.use(handleRequest)
  dhFoodsSubscribe.interceptors.response.use(
    normalizeResponse,
    error => handleError(error, dhFoodsSubscribe)
  )
}

const apiInstance = () => ({
  dhFoods,
  dhFoodsStorage,
  dhFoodsAuth,
  dhFoodsSubscribe
})

export {
  initApi,
  apiInstance,
  resetApi,
  getApiOrigin,
  getServiceBaseUrl
}
